import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useAdjustUserPointsMutation } from 'store/services/admin/adminApi';
import './AdjustPointsModal.css';

interface AdjustPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  currentBalance: number;
  onSuccess: () => void;
}

const AdjustPointsModal: React.FC<AdjustPointsModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  currentBalance,
  onSuccess
}) => {
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [adjustUserPoints, { isLoading }] = useAdjustUserPointsMutation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const numericAmount = amount ? parseInt(amount, 10) : 0;
  const newBalance = currentBalance + numericAmount;
  const isValid =
    numericAmount !== 0 &&
    !isNaN(numericAmount) &&
    reason.trim().length >= 10 &&
    reason.trim().length <= 500 &&
    newBalance >= 0;

  const handleSubmit = async () => {
    if (!isValid) return;

    try {
      await adjustUserPoints({
        userId,
        body: {
          amount: numericAmount,
          reason: reason.trim()
        }
      }).unwrap();

      toast.success(`Successfully adjusted ${username}'s points by ${numericAmount > 0 ? '+' : ''}${numericAmount.toLocaleString()}`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to adjust points:', error);
      toast.error(error.data?.message || 'Failed to adjust points. Please try again.');
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow negative sign, digits only
    if (value === '' || value === '-' || /^-?\d+$/.test(value)) {
      setAmount(value);
    }
  };

  const modalContent = (
    <div className="adjust-points-modal-overlay" onClick={onClose}>
      <div className="adjust-points-modal" onClick={(e) => e.stopPropagation()}>
        <div className="adjust-points-modal-header">
          <h2 className="adjust-points-modal-title">Adjust Point Balance</h2>
          <button className="adjust-points-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="adjust-points-modal-content">
          <div className="user-info-section">
            <div className="info-row">
              <span className="info-label">User:</span>
              <span className="info-value">{username}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Current Balance:</span>
              <span className="info-value points-value">
                {currentBalance.toLocaleString()} points
              </span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="amount" className="form-label">
              Adjustment Amount
              <span className="label-hint">
                (Positive to add, negative to subtract)
              </span>
            </label>
            <input
              id="amount"
              type="text"
              className="form-input"
              placeholder="e.g., 1000 or -500"
              value={amount}
              onChange={handleAmountChange}
              disabled={isLoading}
            />
            {amount && !isNaN(numericAmount) && numericAmount !== 0 && (
              <div className={`balance-preview ${newBalance < 0 ? 'negative' : ''}`}>
                New Balance: {newBalance.toLocaleString()} points
                {newBalance < 0 && <span className="error-text"> (Cannot be negative)</span>}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="reason" className="form-label">
              Reason for Adjustment
              <span className="label-required">*</span>
            </label>
            <textarea
              id="reason"
              className="form-textarea"
              placeholder="Provide a clear explanation for this adjustment (10-500 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
              disabled={isLoading}
            />
            <CharacterCounter
              currentLength={reason.length}
              maxLength={500}
              warningThreshold={450}
            />
            {reason.length > 0 && reason.length < 10 && (
              <div className="validation-hint">
                Reason must be at least 10 characters
              </div>
            )}
          </div>

          <div className="warning-box">
            ⚠️ This action will be recorded in both the admin transaction log and the user's transaction history.
          </div>
        </div>

        <div className="adjust-points-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Adjusting...' : 'Confirm Adjustment'}
            color="purple"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isValid || isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AdjustPointsModal;
